import { Api } from '../services/api.ts';
import type { Usuario } from '../types/index.ts';

let currentUser: Usuario | null = null;

export function getCurrentUser(): Usuario | null {
  return currentUser;
}

export async function checkSession(): Promise<Usuario | null> {
  try {
    const session = await Api.getMe();
    if (session.authenticated && session.user) {
      currentUser = session.user;
      return currentUser;
    }
  } catch (err) {
    console.warn('Sin sesión activa previa o token expirado:', err);
  }
  currentUser = null;
  return null;
}

export function showLoginPage() {
  const loginView = document.getElementById('doctoral-login-view');
  const appContainer = document.getElementById('doctoral-app-container');
  if (loginView) loginView.style.display = 'flex';
  if (appContainer) appContainer.style.display = 'none';

  const inputPass = document.getElementById('input-doctoral-pass') as HTMLInputElement | null;
  if (inputPass) {
    setTimeout(() => inputPass.focus(), 150);
  }
}

export function showAppView(user: Usuario, onLogoutRequest: () => void) {
  const loginView = document.getElementById('doctoral-login-view');
  const appContainer = document.getElementById('doctoral-app-container');
  if (loginView) loginView.style.display = 'none';
  if (appContainer) appContainer.style.display = 'flex';

  renderUserProfile(user, onLogoutRequest);
}

export function initAuth(
  onLoginSuccess: (user: Usuario) => void,
  onLogoutSuccess: () => void
) {
  const loginForm = document.getElementById('form-doctoral-login') as HTMLFormElement | null;
  const inputUser = document.getElementById('input-doctoral-user') as HTMLInputElement | null;
  const inputPass = document.getElementById('input-doctoral-pass') as HTMLInputElement | null;
  const btnTogglePass = document.getElementById('btn-login-toggle-pass') as HTMLButtonElement | null;
  const errorAlert = document.getElementById('login-error-alert') as HTMLElement | null;
  const errorText = document.getElementById('login-error-text') as HTMLElement | null;
  const btnSubmit = document.getElementById('btn-doctoral-login-submit') as HTMLButtonElement | null;

  // Toggle visibilidad de contraseña
  btnTogglePass?.addEventListener('click', () => {
    if (!inputPass) return;
    const isPassword = inputPass.type === 'password';
    inputPass.type = isPassword ? 'text' : 'password';
    btnTogglePass.innerHTML = isPassword
      ? `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`
      : `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;
  });

  function showError(msg: string) {
    if (errorAlert && errorText) {
      errorText.textContent = msg;
      errorAlert.style.display = 'flex';
      errorAlert.classList.remove('shake-error');
      // Forzar reflow para reejecutar la animación de sacudida
      void errorAlert.offsetWidth;
      errorAlert.classList.add('shake-error');
    }
  }

  function hideError() {
    if (errorAlert) {
      errorAlert.style.display = 'none';
      if (errorText) errorText.textContent = '';
    }
  }

  // Escuchar envío del formulario de inicio de sesión
  loginForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!inputUser || !inputPass) return;

    const username = inputUser.value.trim();
    const password = inputPass.value;

    if (!username || !password) {
      showError('Por favor complete su usuario doctoral y contraseña.');
      return;
    }

    hideError();
    if (btnSubmit) {
      btnSubmit.disabled = true;
      btnSubmit.innerHTML = `
        <span class="login-spinner"></span>
        <span>Validando credenciales criptográficas...</span>
      `;
    }

    try {
      const res = await Api.login(username, password);
      if (res.ok && res.user) {
        currentUser = res.user;
        showAppView(currentUser, onLogoutSuccess);
        onLoginSuccess(currentUser);
      } else {
        showError(res.error || 'Credenciales doctorales no válidas.');
      }
    } catch (err: any) {
      showError(err.message || 'Error de conexión o autenticación rechazada por el servidor.');
    } finally {
      if (btnSubmit) {
        btnSubmit.disabled = false;
        btnSubmit.innerHTML = `
          <span class="btn-text">Verificar y Acceder al Atlas</span>
          <svg class="btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        `;
      }
    }
  });

  // Escuchar evento global de expiración de sesión (401 en cualquier llamada)
  window.addEventListener('doctoral:session_expired', () => {
    currentUser = null;
    showLoginPage();
    showError('Su sesión ha expirado por seguridad o inactividad. Por favor autentíquese nuevamente.');
    onLogoutSuccess();
  });
}

function renderUserProfile(user: Usuario, onLogoutRequest: () => void) {
  const profileContainer = document.getElementById('header-user-profile');
  if (!profileContainer) return;

  profileContainer.innerHTML = `
    <div class="user-badge-chip" title="Sesión activa y verificada con SQLite">
      <div class="user-avatar-dot">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
      </div>
      <div class="user-info-text">
        <span class="user-name">${user.nombre_completo}</span>
        <span class="user-role">${user.username} • UZ Doctorado</span>
      </div>
      <button id="btn-user-logout" class="btn-logout" title="Cerrar sesión de investigadora">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      </button>
    </div>
  `;

  document.getElementById('btn-user-logout')?.addEventListener('click', async () => {
    if (confirm('¿Desea cerrar la sesión activa de la investigación doctoral?')) {
      await Api.logout();
      currentUser = null;
      profileContainer.innerHTML = '';
      showLoginPage();
      onLogoutRequest();
    }
  });
}
